"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface Comment {
  id: number;
  parentId: number | null;
  name: string;
  email: string;
  body: string;
  date: string;
  status: "approved" | "pending" | "rejected";
  replies?: Comment[];
}

const INITIAL_COMMENTS: Comment[] = [
  {
    id: 1,
    parentId: null,
    name: "Alexandra Reeves",
    email: "a.reeves@example.com",
    body: "This is an incredibly insightful article. The data-driven approach described here aligns perfectly with what we have been trying to implement at our firm. Would love to hear more about the specific analytics platforms StanDave uses.",
    date: "March 3, 2025",
    status: "approved",
  },
  {
    id: 2,
    parentId: null,
    name: "Kwame Asante",
    email: "k.asante@example.com",
    body: "Great read. The section on strategic rebalancing really resonated with me. We went through a major portfolio overhaul last year and the quarterly review cadence was a game-changer for us.",
    date: "March 4, 2025",
    status: "approved",
  },
  {
    id: 3,
    parentId: 2,
    name: "Priya Nair",
    email: "p.nair@example.com",
    body: "Totally agree, Kwame. The quarterly review process forces you to be disciplined rather than reactive. We combined it with monthly check-ins and the results have been excellent.",
    date: "March 4, 2025",
    status: "approved",
  },
  {
    id: 4,
    parentId: null,
    name: "Thomas Bergmann",
    email: "t.bergmann@example.com",
    body: "Waiting for moderation review.",
    date: "March 5, 2025",
    status: "pending",
  },
];

interface CommentFormData {
  name: string;
  email: string;
  body: string;
}

const emptyForm: CommentFormData = { name: "", email: "", body: "" };

function buildTree(comments: Comment[]): Comment[] {
  const map: Record<number, Comment> = {};
  const roots: Comment[] = [];
  comments.forEach((c) => {
    map[c.id] = { ...c, replies: [] };
  });
  comments.forEach((c) => {
    if (c.parentId !== null && map[c.parentId]) {
      map[c.parentId].replies!.push(map[c.id]);
    } else if (c.parentId === null) {
      roots.push(map[c.id]);
    }
  });
  return roots;
}

interface CommentCardProps {
  comment: Comment;
  isAdmin: boolean;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onDelete: (id: number) => void;
  onReplySubmit: (parentId: number, data: CommentFormData) => void;
  depth?: number;
}

function CommentCard({
  comment,
  isAdmin,
  onApprove,
  onReject,
  onDelete,
  onReplySubmit,
  depth = 0,
}: CommentCardProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyForm, setReplyForm] = useState<CommentFormData>(emptyForm);
  const [replyErrors, setReplyErrors] = useState<Partial<CommentFormData>>({});
  const [replySuccess, setReplySuccess] = useState(false);

  const statusStyles: Record<string, string> = {
    approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  };

  const validateReply = () => {
    const errs: Partial<CommentFormData> = {};
    if (!replyForm.name.trim()) errs.name = "Name is required";
    if (!replyForm.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyForm.email))
      errs.email = "Enter a valid email";
    if (!replyForm.body.trim()) errs.body = "Reply cannot be empty";
    return errs;
  };

  const handleReplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateReply();
    if (Object.keys(errs).length > 0) {
      setReplyErrors(errs);
      return;
    }
    onReplySubmit(comment.id, replyForm);
    setReplyForm(emptyForm);
    setReplyErrors({});
    setShowReplyForm(false);
    setReplySuccess(true);
    setTimeout(() => setReplySuccess(false), 4000);
  };

  const isHidden = !isAdmin && comment.status !== "approved";
  if (isHidden) return null;

  return (
    <div className={`${depth > 0 ? "ml-6 md:ml-10 pl-4 border-l border-[rgba(201,168,76,0.15)]" : ""}  mb-4`}>
      <div
        className={`rounded-xl border p-5 transition-all ${
          comment.status === "rejected" ?"border-red-500/15 bg-red-500/5 opacity-60"
            : comment.status === "pending" ?"border-amber-500/15 bg-amber-500/5" :"border-[rgba(201,168,76,0.12)] bg-[rgba(255,255,255,0.02)]"
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center flex-shrink-0">
              <span className="font-display text-sm font-600 text-gold">
                {comment.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-display text-sm font-600 text-cream">{comment.name}</p>
              <p className="text-[11px] font-body text-cream/35">{comment.date}</p>
            </div>
          </div>
          {isAdmin && (
            <span
              className={`text-[10px] font-display uppercase tracking-wider px-2.5 py-1 rounded-full border ${statusStyles[comment.status]}`}
            >
              {comment.status}
            </span>
          )}
        </div>

        {/* Body */}
        <p className="text-[14px] font-body text-cream/65 leading-relaxed mb-4">
          {comment.body}
        </p>

        {/* Actions row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Reply button — only for approved comments */}
          {comment.status === "approved" && (
            <button
              onClick={() => setShowReplyForm((v) => !v)}
              className="flex items-center gap-1.5 text-[12px] font-display text-cream/40 hover:text-gold transition-colors"
            >
              <Icon name="ChatBubbleLeftIcon" size={13} className="" />
              {showReplyForm ? "Cancel" : "Reply"}
            </button>
          )}

          {/* Moderation controls */}
          {isAdmin && (
            <div className="flex items-center gap-2 ml-auto">
              {comment.status !== "approved" && (
                <button
                  onClick={() => onApprove(comment.id)}
                  className="flex items-center gap-1 text-[11px] font-display px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors"
                >
                  <Icon name="CheckIcon" size={11} className="" />
                  Approve
                </button>
              )}
              {comment.status !== "rejected" && (
                <button
                  onClick={() => onReject(comment.id)}
                  className="flex items-center gap-1 text-[11px] font-display px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                >
                  <Icon name="XMarkIcon" size={11} className="" />
                  Reject
                </button>
              )}
              <button
                onClick={() => onDelete(comment.id)}
                className="flex items-center gap-1 text-[11px] font-display px-2.5 py-1 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
              >
                <Icon name="TrashIcon" size={11} className="" />
                Delete
              </button>
            </div>
          )}
        </div>

        {/* Reply success */}
        {replySuccess && (
          <p className="mt-3 text-[12px] font-body text-emerald-400">
            ✓ Your reply has been submitted and is awaiting moderation.
          </p>
        )}

        {/* Inline reply form */}
        {showReplyForm && (
          <form
            onSubmit={handleReplySubmit}
            className="mt-4 pt-4 border-t border-[rgba(201,168,76,0.10)] space-y-3"
          >
            <p className="text-[11px] font-display uppercase tracking-wider text-gold/60 mb-2">
              Reply to {comment.name}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <input
                  type="text"
                  placeholder="Your name *"
                  value={replyForm.name}
                  onChange={(e) => {
                    setReplyForm((f) => ({ ...f, name: e.target.value }));
                    if (replyErrors.name) setReplyErrors((er) => ({ ...er, name: undefined }));
                  }}
                  className={`w-full bg-[rgba(255,255,255,0.04)] border rounded-lg px-3 py-2 text-[13px] font-body text-cream placeholder-cream/25 focus:outline-none focus:border-gold/50 transition-colors ${
                    replyErrors.name ? "border-red-400/60" : "border-[rgba(201,168,76,0.15)]"
                  }`}
                />
                {replyErrors.name && (
                  <p className="text-[11px] text-red-400 mt-1">{replyErrors.name}</p>
                )}
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Your email *"
                  value={replyForm.email}
                  onChange={(e) => {
                    setReplyForm((f) => ({ ...f, email: e.target.value }));
                    if (replyErrors.email) setReplyErrors((er) => ({ ...er, email: undefined }));
                  }}
                  className={`w-full bg-[rgba(255,255,255,0.04)] border rounded-lg px-3 py-2 text-[13px] font-body text-cream placeholder-cream/25 focus:outline-none focus:border-gold/50 transition-colors ${
                    replyErrors.email ? "border-red-400/60" : "border-[rgba(201,168,76,0.15)]"
                  }`}
                />
                {replyErrors.email && (
                  <p className="text-[11px] text-red-400 mt-1">{replyErrors.email}</p>
                )}
              </div>
            </div>
            <div>
              <textarea
                rows={3}
                placeholder="Write your reply..."
                value={replyForm.body}
                onChange={(e) => {
                  setReplyForm((f) => ({ ...f, body: e.target.value }));
                  if (replyErrors.body) setReplyErrors((er) => ({ ...er, body: undefined }));
                }}
                className={`w-full bg-[rgba(255,255,255,0.04)] border rounded-lg px-3 py-2 text-[13px] font-body text-cream placeholder-cream/25 focus:outline-none focus:border-gold/50 transition-colors resize-none ${
                  replyErrors.body ? "border-red-400/60" : "border-[rgba(201,168,76,0.15)]"
                }`}
              />
              {replyErrors.body && (
                <p className="text-[11px] text-red-400 mt-1">{replyErrors.body}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gold text-[#0C1B33] font-display text-[12px] font-600 uppercase tracking-wider hover:bg-gold/90 transition-colors"
              >
                <Icon name="PaperAirplaneIcon" size={12} className="" />
                Post Reply
              </button>
              <button
                type="button"
                onClick={() => { setShowReplyForm(false); setReplyErrors({}); setReplyForm(emptyForm); }}
                className="text-[12px] font-display text-cream/35 hover:text-cream/60 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Nested replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-3">
          {comment.replies.map((reply) => (
            <CommentCard
              key={reply.id}
              comment={reply}
              isAdmin={isAdmin}
              onApprove={onApprove}
              onReject={onReject}
              onDelete={onDelete}
              onReplySubmit={onReplySubmit}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CommentsSection() {
  const [comments, setComments] = useState<Comment[]>(INITIAL_COMMENTS);
  const [isAdmin, setIsAdmin] = useState(false);
  const [form, setForm] = useState<CommentFormData>(emptyForm);
  const [errors, setErrors] = useState<Partial<CommentFormData>>({});
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const validate = () => {
    const errs: Partial<CommentFormData> = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Enter a valid email address";
    if (!form.body.trim()) errs.body = "Comment cannot be empty";
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    const newComment: Comment = {
      id: Date.now(),
      parentId: null,
      name: form.name.trim(),
      email: form.email.trim(),
      body: form.body.trim(),
      date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
      status: "pending",
    };
    setComments((prev) => [...prev, newComment]);
    setForm(emptyForm);
    setErrors({});
    setSubmitted(true);
    showToast("Comment submitted! It will appear after moderation.");
    setTimeout(() => setSubmitted(false), 5000);
  };

  const handleReplySubmit = (parentId: number, data: CommentFormData) => {
    const newReply: Comment = {
      id: Date.now(),
      parentId,
      name: data.name.trim(),
      email: data.email.trim(),
      body: data.body.trim(),
      date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
      status: "pending",
    };
    setComments((prev) => [...prev, newReply]);
    showToast("Reply submitted! It will appear after moderation.");
  };

  const handleApprove = (id: number) => {
    setComments((prev) => prev.map((c) => (c.id === id ? { ...c, status: "approved" } : c)));
    showToast("Comment approved.");
  };

  const handleReject = (id: number) => {
    setComments((prev) => prev.map((c) => (c.id === id ? { ...c, status: "rejected" } : c)));
    showToast("Comment rejected.");
  };

  const handleDelete = (id: number) => {
    setComments((prev) => prev.filter((c) => c.id !== id && c.parentId !== id));
    showToast("Comment deleted.");
  };

  const tree = buildTree(comments);
  const approvedCount = comments.filter((c) => c.status === "approved").length;
  const pendingCount = comments.filter((c) => c.status === "pending").length;

  return (
    <section className="max-w-4xl mx-auto px-6 pb-20">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl bg-[#0a1628] border border-gold/30 shadow-xl">
          <Icon name="CheckCircleIcon" size={16} className="text-gold flex-shrink-0" />
          <p className="text-[13px] font-body text-cream">{toast}</p>
        </div>
      )}

      {/* Section header */}
      <div className="flex items-center justify-between gap-4 mb-8 pt-10 border-t border-[rgba(201,168,76,0.12)]">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-gold rounded-full" />
          <h2 className="font-display text-lg font-600 text-cream">
            Reader Comments
            <span className="ml-2 text-[13px] font-body text-cream/35 font-400">
              ({approvedCount})
            </span>
          </h2>
        </div>
        {/* Admin toggle */}
        <button
          onClick={() => setIsAdmin((v) => !v)}
          className={`flex items-center gap-2 text-[11px] font-display uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-colors ${
            isAdmin
              ? "bg-gold/15 text-gold border-gold/30" :"bg-[rgba(255,255,255,0.03)] text-cream/35 border-[rgba(255,255,255,0.08)] hover:border-gold/20 hover:text-cream/60"
          }`}
        >
          <Icon name="ShieldCheckIcon" size={12} className="" />
          {isAdmin ? "Admin Mode" : "Admin"}
        </button>
      </div>

      {/* Admin stats bar */}
      {isAdmin && (
        <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-[rgba(201,168,76,0.04)] border border-[rgba(201,168,76,0.12)]">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-[12px] font-body text-cream/50">
              {approvedCount} approved
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-[12px] font-body text-cream/50">
              {pendingCount} pending
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            <span className="text-[12px] font-body text-cream/50">
              {comments.filter((c) => c.status === "rejected").length} rejected
            </span>
          </div>
        </div>
      )}

      {/* Threaded comments */}
      {tree.length > 0 ? (
        <div className="space-y-4 mb-10">
          {tree.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              isAdmin={isAdmin}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDelete}
              onReplySubmit={handleReplySubmit}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 mb-10 rounded-xl border border-[rgba(201,168,76,0.08)] bg-[rgba(255,255,255,0.01)]">
          <Icon name="ChatBubbleLeftRightIcon" size={36} className="text-gold/15 mb-3" />
          <p className="text-[13px] font-body text-cream/30">No comments yet. Be the first to share your thoughts.</p>
        </div>
      )}

      {/* Comment submission form */}
      <div className="rounded-xl border border-[rgba(201,168,76,0.15)] bg-[rgba(201,168,76,0.02)] p-6">
        <h3 className="font-display text-base font-600 text-cream mb-1">
          Leave a Comment
        </h3>
        <p className="text-[12px] font-body text-cream/35 mb-5">
          Your email address will not be published. All comments are moderated.
        </p>

        {submitted && (
          <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <Icon name="CheckCircleIcon" size={14} className="text-emerald-400" />
            <p className="text-[12px] font-body text-emerald-400">
              Thank you! Your comment has been submitted and is awaiting moderation.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-[11px] font-display uppercase tracking-wider text-cream/40 mb-1.5">
                Name <span className="text-gold">*</span>
              </label>
              <input
                type="text"
                placeholder="Your full name"
                value={form.name}
                onChange={(e) => {
                  setForm((f) => ({ ...f, name: e.target.value }));
                  if (errors.name) setErrors((er) => ({ ...er, name: undefined }));
                }}
                className={`w-full bg-[rgba(255,255,255,0.04)] border rounded-lg px-4 py-2.5 text-[14px] font-body text-cream placeholder-cream/20 focus:outline-none focus:border-gold/50 transition-colors ${
                  errors.name ? "border-red-400/60" : "border-[rgba(201,168,76,0.15)]"
                }`}
              />
              {errors.name && (
                <p className="text-[11px] text-red-400 mt-1 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={11} className="" />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-[11px] font-display uppercase tracking-wider text-cream/40 mb-1.5">
                Email <span className="text-gold">*</span>
              </label>
              <input
                type="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={(e) => {
                  setForm((f) => ({ ...f, email: e.target.value }));
                  if (errors.email) setErrors((er) => ({ ...er, email: undefined }));
                }}
                className={`w-full bg-[rgba(255,255,255,0.04)] border rounded-lg px-4 py-2.5 text-[14px] font-body text-cream placeholder-cream/20 focus:outline-none focus:border-gold/50 transition-colors ${
                  errors.email ? "border-red-400/60" : "border-[rgba(201,168,76,0.15)]"
                }`}
              />
              {errors.email && (
                <p className="text-[11px] text-red-400 mt-1 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={11} className="" />
                  {errors.email}
                </p>
              )}
            </div>
          </div>

          {/* Comment textarea */}
          <div>
            <label className="block text-[11px] font-display uppercase tracking-wider text-cream/40 mb-1.5">
              Comment <span className="text-gold">*</span>
            </label>
            <textarea
              rows={5}
              placeholder="Share your thoughts on this article..."
              value={form.body}
              onChange={(e) => {
                setForm((f) => ({ ...f, body: e.target.value }));
                if (errors.body) setErrors((er) => ({ ...er, body: undefined }));
              }}
              className={`w-full bg-[rgba(255,255,255,0.04)] border rounded-lg px-4 py-2.5 text-[14px] font-body text-cream placeholder-cream/20 focus:outline-none focus:border-gold/50 transition-colors resize-none ${
                errors.body ? "border-red-400/60" : "border-[rgba(201,168,76,0.15)]"
              }`}
            />
            {errors.body && (
              <p className="text-[11px] text-red-400 mt-1 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={11} className="" />
                {errors.body}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gold text-[#0C1B33] font-display text-[13px] font-600 uppercase tracking-wider hover:bg-gold/90 active:scale-95 transition-all"
          >
            <Icon name="PaperAirplaneIcon" size={14} className="" />
            Post Comment
          </button>
        </form>
      </div>
    </section>
  );
}
